import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getPosts, getPost, getPostsByCategory, getCategories } from '../components/api'
import { BLOG_CONFIG } from '../config/blog'

const BlogContext = createContext()

const CACHE_KEY = 'blog_posts_cache'
const CACHE_TIMESTAMP_KEY = 'blog_posts_cache_timestamp'
const PAGES_CACHE_KEY = 'blog_pages_cache'
const CATEGORIES_CACHE_KEY = 'blog_categories_cache'

export function BlogProvider({ children }) {
  const [postsCache, setPostsCache] = useState({})
  const [pagesCache, setPagesCache] = useState({}) // { 'all_0': { posts, nextCursor }, 'cat_1_0': {...} }
  const [categoriesCache, setCategoriesCache] = useState({})
  const [loading, setLoading] = useState(false)

  // Helper: Sort posts by date (newest first)
  const sortPostsByDate = (posts) => 
    [...posts].sort((a, b) => new Date(b.date) - new Date(a.date))

  // Helper: Clear all cache data
  const clearCacheStorage = () => {
    sessionStorage.removeItem(CACHE_KEY)
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY)
    sessionStorage.removeItem(PAGES_CACHE_KEY)
    sessionStorage.removeItem(CATEGORIES_CACHE_KEY)
  }

  // Fetch and cache categories
  const fetchCategories = useCallback(async () => {
    if (Object.keys(categoriesCache).length > 0) {
      return categoriesCache
    }

    try {
      const categories = await getCategories()
      const cache = {}
      categories.forEach(cat => {
        cache[cat.id] = cat
      })
      setCategoriesCache(cache)
      sessionStorage.setItem(CATEGORIES_CACHE_KEY, JSON.stringify(cache))
      return cache
    } catch (error) {
      console.error('Error fetching categories:', error)
      throw error
    }
  }, [categoriesCache])

  // Helper: Join category data with posts
  const joinCategoryData = (posts, categories) => {
    return posts.map(post => ({
      ...post,
      category: categories[post.categoryId]?.name || 'Sin categoría',
      categorySlug: categories[post.categoryId]?.slug || ''
    }))
  }

  // Load cache from sessionStorage on mount
  useEffect(() => {
    const cached = sessionStorage.getItem(CACHE_KEY)
    const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY)
    const cachedPages = sessionStorage.getItem(PAGES_CACHE_KEY)
    const cachedCategories = sessionStorage.getItem(CATEGORIES_CACHE_KEY)
    
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp)
      if (age < BLOG_CONFIG.CACHE_DURATION) {
        if (cached) setPostsCache(JSON.parse(cached))
        if (cachedPages) setPagesCache(JSON.parse(cachedPages))
        if (cachedCategories) setCategoriesCache(JSON.parse(cachedCategories))
      } else {
        clearCacheStorage()
      }
    }
  }, [])

  // Save posts cache to sessionStorage
  const updatePostsCache = (newCache) => {
    setPostsCache(newCache)
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache))
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
  }

  // Save pages cache to sessionStorage
  const updatePagesCache = (newCache) => {
    setPagesCache(newCache)
    sessionStorage.setItem(PAGES_CACHE_KEY, JSON.stringify(newCache))
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString())
  }

  // Generate cache key for a page
  const getPageCacheKey = (categoryId, pageIndex) => {
    return categoryId ? `cat_${categoryId}_${pageIndex}` : `all_${pageIndex}`
  }

  // Fetch posts for a specific page
  const fetchPostsPage = useCallback(async (categoryId = null, pageIndex = 0, cursor = null) => {
    const categories = await fetchCategories()
    const cacheKey = getPageCacheKey(categoryId, pageIndex)

    // Check if page is cached
    if (pagesCache[cacheKey]) {
      const cachedPage = pagesCache[cacheKey]
      return {
        posts: joinCategoryData(cachedPage.posts, categories),
        nextCursor: cachedPage.nextCursor,
        hasMore: cachedPage.hasMore,
        pageIndex
      }
    }

    // Need to fetch from API
    setLoading(true)
    try {
      let result
      if (categoryId) {
        result = await getPostsByCategory(categoryId, cursor)
      } else {
        result = await getPosts(cursor)
      }

      const { posts, nextCursor, hasMore } = result

      // Cache individual posts
      const newPostsCache = { ...postsCache }
      posts.forEach(post => {
        newPostsCache[post.id] = post
      })
      updatePostsCache(newPostsCache)

      // Cache the page
      const newPagesCache = { ...pagesCache }
      newPagesCache[cacheKey] = { posts, nextCursor, hasMore }
      updatePagesCache(newPagesCache)

      return {
        posts: joinCategoryData(posts, categories),
        nextCursor,
        hasMore,
        pageIndex
      }
    } catch (error) {
      console.error('Error fetching posts page:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [fetchCategories, pagesCache, postsCache])

  // Get cursor for a specific page (needed for navigating to next page)
  const getCursorForPage = useCallback((categoryId, pageIndex) => {
    if (pageIndex === 0) return null
    const prevPageKey = getPageCacheKey(categoryId, pageIndex - 1)
    return pagesCache[prevPageKey]?.nextCursor || null
  }, [pagesCache])

  // Check if a page is cached
  const isPageCached = useCallback((categoryId, pageIndex) => {
    const cacheKey = getPageCacheKey(categoryId, pageIndex)
    return !!pagesCache[cacheKey]
  }, [pagesCache])

  const fetchSinglePost = useCallback(async (id) => {
    const categories = await fetchCategories()
    const numericId = Number(id)
    
    // Check cache first
    if (postsCache[numericId] && postsCache[numericId].content) {
      const post = postsCache[numericId]
      return {
        ...post,
        category: categories[post.categoryId]?.name || 'Sin categoría',
        categorySlug: categories[post.categoryId]?.slug || ''
      }
    }

    // Fetch from API
    setLoading(true)
    try {
      const post = await getPost(id)
      updatePostsCache({ ...postsCache, [post.id]: post })
      return {
        ...post,
        category: categories[post.categoryId]?.name || 'Sin categoría',
        categorySlug: categories[post.categoryId]?.slug || ''
      }
    } catch (error) {
      console.error('Error fetching post:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [fetchCategories, postsCache])

  const clearCache = useCallback(() => {
    setPostsCache({})
    setPagesCache({})
    setCategoriesCache({})
    clearCacheStorage()
  }, [])

  const getCategoriesList = useCallback(async () => {
    const categories = await fetchCategories()
    return Object.values(categories).sort((a, b) => a.name.localeCompare(b.name))
  }, [fetchCategories])

  return (
    <BlogContext.Provider value={{
      postsCache,
      loading,
      fetchPostsPage,
      getCursorForPage,
      isPageCached,
      fetchSinglePost,
      clearCache,
      getCategoriesList
    }}>
      {children}
    </BlogContext.Provider>
  )
}

export function useBlog() {
  const context = useContext(BlogContext)
  if (!context) {
    throw new Error('useBlog must be used within a BlogProvider')
  }
  return context
}
